import { useAuth } from "@clerk/clerk-react";
import { createApiClient } from "./client";
import type { Record, RecordResponse } from "../../types";
import type { DocumentRecord } from "../../types/record";
import type { SearchResponse } from "../../types/search";
import { PAGE_SIZE } from "../constants";
import type { DeltaStatic } from "quill";
import { appConfig } from "../../config/appConfig";

export interface CreateRecordPayload {
  title: string;
  content: string;
  formatted_content: DeltaStatic;
  meta?: string;
  references: Array<{
    reference_type_id: number;
    reference: string;
  }>;
}

export interface UpdateRecordPayload {
  title?: string;
  content?: string;
  formatted_content?: DeltaStatic;
  meta?: string;
  references?: Array<{
    reference_type_id: number;
    reference: string;
  }>;
}

export interface ReferenceType {
  id: string;
  name: string;
}

export interface RagSource {
  id: string;
  title?: string;
  score: number;
  content?: string;
}

export interface RagResponse {
  text: string;
  sources: RagSource[];
  search_id?: number;
  execution_time_ms: number;
  error?: string;
  session_id?: string;
  generation_id?: string;
}

export function useRecordsApi() {
  const { getToken } = useAuth();
  const api = createApiClient(() => getToken());

  return {
    async getAllRecords(
      page: number = 0,
      size: number = 10,
      orderBy?: string,
      orderDirection: "asc" | "desc" = "asc",
      showDeleted: boolean = false
    ): Promise<RecordResponse> {
      const response = await api.get(`/documents`, {
        params: {
          page,
          size,
          order_by: orderBy,
          order_direction: orderDirection,
          show_deleted: showDeleted,
        },
      });
      return response.data;
    },

    async getRecord(id: string): Promise<DocumentRecord> {
      const response = await api.get(`/documents/${id}`);
      return response.data;
    },

    async getDocumentContent(id: string): Promise<any> {
      const response = await api.get(`/documents/${id}/content`);
      return response.data;
    },

    async updateRecord(
      id: string,
      updates: UpdateRecordPayload
    ): Promise<Record> {
      const response = await api.put(`/documents/${id}`, updates);
      return response.data;
    },

    async deleteRecord(id: string): Promise<void> {
      await api.delete(`/documents/${id}`);
    },

    async unarchiveRecord(id: number): Promise<void> {
      await api.post(`/documents/${id}/unarchive`);
    },

    async createRecord(record: CreateRecordPayload): Promise<Record> {
      const response = await api.post("/documents", record);
      return response.data;
    },

    async searchRecords(
      query: string,
      page: number = 0,
      pageSize: number = PAGE_SIZE,
      semantic: boolean = true,
      limit: number = 5
    ): Promise<SearchResponse & { search_id?: string }> {
      const response = await api.get("/search", {
        params: { q: query, page, size: pageSize, semantic, limit },
      });
      return response.data;
    },

    async ragSearch(
      query: string,
      contextLimit: number = appConfig.rag.defaultContextLimit,
      maxSources: number = 5
    ): Promise<RagResponse> {
      const response = await api.get("/search/rag", {
        params: {
          q: query,
          context_limit: contextLimit,
          max_sources: maxSources,
          use_reranking: true,
        },
      });
      return response.data;
    },

    async generateRagResponse(
      query: string,
      onChunk?: (chunk: string) => void,
      contextLimit: number = appConfig.rag.defaultContextLimit,
      maxSources: number = 5
    ): Promise<RagResponse | string> {
      // If streaming is disabled or no onChunk handler is provided, use non-streaming approach
      if (!appConfig.rag.useStreaming || !onChunk) {
        return await this.ragSearch(query, contextLimit, maxSources);
      }

      // Otherwise use streaming
      return this.streamRagSearch(query, onChunk, contextLimit, maxSources);
    },

    async streamRagSearch(
      query: string,
      onChunk: (chunk: string) => void,
      contextLimit: number = appConfig.rag.defaultContextLimit,
      maxSources: number = 5
    ): Promise<string> {
      const MAX_RETRIES = appConfig.rag.maxRetries;
      const RETRY_DELAY = appConfig.rag.retryDelay;
      let retryCount = 0;
      let fullResponse = "";

      const attemptFetch = async (): Promise<string> => {
        try {
          console.log("Sending streaming RAG request:", query);

          const response = await fetch(
            `${
              import.meta.env.VITE_API_URL
            }/search/rag/stream?q=${encodeURIComponent(
              query
            )}&context_limit=${contextLimit}&max_sources=${maxSources}`,
            {
              headers: {
                Accept: "text/event-stream",
                Authorization: `Bearer ${await getToken()}`,
              },
            }
          );

          if (!response.ok) {
            const errorText = await response.text();
            throw new Error(
              `HTTP error! Status: ${response.status}, Details: ${errorText}`
            );
          }

          if (!response.body) {
            throw new Error("Response body is null");
          }

          const reader = response.body.getReader();
          const decoder = new TextDecoder();
          let buffer = "";

          while (true) {
            const { value, done } = await reader.read();
            if (done) break;

            const text = decoder.decode(value, { stream: true });
            buffer += text;

            // Process complete lines - SSE uses \n\n as separator
            const events = buffer.split("\n\n");
            buffer = events.pop() || ""; // Keep the last incomplete event in buffer

            for (const event of events) {
              // Handle different SSE formats
              const lines = event.split("\n");
              let data = "";

              for (const line of lines) {
                if (line.startsWith("data: ")) {
                  data += line.slice(6); // Remove 'data: ' prefix
                } else if (line.trim() === "data:") {
                  // Empty data line - preserve it as it represents important whitespace/newlines
                  data += "";
                } else if (line.startsWith("data:")) {
                  data += line.slice(5); // Handle data: without space
                } else if (
                  line.trim() &&
                  !line.startsWith("id:") &&
                  !line.startsWith("event:") &&
                  !line.startsWith("retry:")
                ) {
                  // Capture content that's not prefixed with "data:" but isn't SSE metadata
                  data += line;
                }
              }

              // Important: Don't filter out empty strings as they represent important formatting
              // Only skip if the original line had no data at all
              if (data !== undefined) {
                onChunk(data);
                fullResponse += data;
              }
            }
          }

          // Process any remaining data in buffer
          if (buffer.trim()) {
            const lines = buffer.split("\n");
            let data = "";

            for (const line of lines) {
              if (line.startsWith("data: ")) {
                data += line.slice(6);
              } else if (line.trim() === "data:") {
                // Empty data line - preserve it as it represents important whitespace/newlines
                data += "";
              } else if (line.startsWith("data:")) {
                data += line.slice(5);
              } else if (
                line.trim() &&
                !line.startsWith("id:") &&
                !line.startsWith("event:") &&
                !line.startsWith("retry:")
              ) {
                // Capture content that's not prefixed with "data:" but isn't SSE metadata
                data += line;
              }
            }

            // Same here - preserve all data including empty strings for formatting
            if (data !== undefined) {
              onChunk(data);
              fullResponse += data;
            }
          }

          return fullResponse;
        } catch (error) {
          console.error(
            `Streaming RAG Search Error (attempt ${
              retryCount + 1
            }/${MAX_RETRIES}):`,
            error
          );

          if (retryCount < MAX_RETRIES - 1) {
            retryCount++;
            console.log(`Retrying in ${RETRY_DELAY}ms...`);
            await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY));
            return attemptFetch();
          }

          throw error;
        }
      };

      return attemptFetch();
    },

    async getReferenceTypes() {
      const response = await api.get<ReferenceType[]>("/references/types");
      return response.data;
    },

    generateConversationalResponse: async (
      query: string,
      sessionId?: string | null,
      contextLimit: number = 5,
      onChunk?: (chunk: string) => void
    ): Promise<RagResponse> => {
      // If streaming is requested, use the streaming endpoint
      if (onChunk) {
        console.log("Sending streaming conversational RAG request:", query);

        const response = await fetch(
          `${
            import.meta.env.VITE_API_URL
          }/search/rag/conversational/stream?query=${encodeURIComponent(
            query
          )}&context_limit=${contextLimit}${
            sessionId ? `&session_id=${encodeURIComponent(sessionId)}` : ""
          }`,
          {
            method: "POST",
            headers: {
              Accept: "text/event-stream",
              Authorization: `Bearer ${await getToken()}`,
            },
            body: JSON.stringify({
              session_id: sessionId,
              context_limit: contextLimit,
            }),
          }
        );

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(
            `HTTP error! Status: ${response.status}, Details: ${errorText}`
          );
        }

        if (!response.body) {
          throw new Error("Response body is null");
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let fullResponse = "";
        let buffer = "";

        while (true) {
          const { value, done } = await reader.read();
          if (done) break;

          const text = decoder.decode(value, { stream: true });
          buffer += text;

          // Process complete lines - SSE uses \n\n as separator
          const events = buffer.split("\n\n");
          buffer = events.pop() || ""; // Keep the last incomplete event in buffer

          for (const event of events) {
            // Handle different SSE formats
            const lines = event.split("\n");
            let data = "";

            for (const line of lines) {
              if (line.startsWith("data: ")) {
                data += line.slice(6); // Remove 'data: ' prefix
              } else if (line.trim() === "data:") {
                // Empty data line - preserve it as it represents important whitespace/newlines
                data += "";
              } else if (line.startsWith("data:")) {
                data += line.slice(5); // Handle data: without space
              } else if (
                line.trim() &&
                !line.startsWith("id:") &&
                !line.startsWith("event:") &&
                !line.startsWith("retry:")
              ) {
                // Capture content that's not prefixed with "data:" but isn't SSE metadata
                data += line;
              }
            }

            // Important: Don't filter out empty strings as they represent important formatting
            // Only skip if the original line had no data at all
            if (data !== undefined) {
              onChunk(data);
              fullResponse += data;
            }
          }
        }

        // Process any remaining data in buffer
        if (buffer.trim()) {
          const lines = buffer.split("\n");
          let data = "";

          for (const line of lines) {
            if (line.startsWith("data: ")) {
              data += line.slice(6);
            } else if (line.trim() === "data:") {
              // Empty data line - preserve it as it represents important whitespace/newlines
              data += "";
            } else if (line.startsWith("data:")) {
              data += line.slice(5);
            } else if (
              line.trim() &&
              !line.startsWith("id:") &&
              !line.startsWith("event:") &&
              !line.startsWith("retry:")
            ) {
              // Capture content that's not prefixed with "data:" but isn't SSE metadata
              data += line;
            }
          }

          // Same here - preserve all data including empty strings for formatting
          if (data !== undefined) {
            onChunk(data);
            fullResponse += data;
          }
        }

        // Return a mock RagResponse for streaming (sources will be handled by the chunk callback)
        return {
          text: fullResponse,
          sources: [],
          execution_time_ms: 0,
        };
      }

      // Non-streaming case - use the regular endpoint
      const response = await api.post<RagResponse>(
        "/search/rag/conversational",
        {},
        {
          params: {
            query,
            session_id: sessionId,
            context_limit: contextLimit,
          },
        }
      );
      return response.data;
    },
  };
}
